import { KeyRound, LoaderCircle, Mail, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { dashboardApi } from "../api";
import type { SessionUser } from "../types";
import { useT } from "../i18n";

/**
 * Shown INSTEAD of the app while an account still holds a password somebody else
 * generated. There is no way past it and nothing to dismiss — the server refuses
 * every other route anyway, so offering a "later" would just produce a broken app.
 */
export default function ChangePasswordPage({
  user,
  onChanged,
  onLogout,
}: {
  user: SessionUser;
  onChanged: (user: SessionUser) => void;
  onLogout: () => void;
}) {
  const t = useT();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // null = nothing picked yet. Deliberately NOT defaulting to false: a pre-ticked
  // "no" is still a box the person never chose, and consent nobody gave is the
  // whole thing this screen exists to prevent.
  const [emailOptIn, setEmailOptIn] = useState<boolean | null>(user.emailOptIn);

  // Asked once, of clients only. Staff are notified over Telegram, so there is
  // nothing for them to consent to and no question to answer.
  const asksConsent = user.role === "client" && user.emailOptIn === null;

  const tooShort = newPassword.length > 0 && newPassword.length < 12;
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit = currentPassword && newPassword.length >= 12 && newPassword === confirmPassword
    && (!asksConsent || emailOptIn !== null) && !busy;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    try {
      const result = await dashboardApi.changePassword(
        currentPassword, newPassword, asksConsent ? emailOptIn ?? undefined : undefined,
      );
      onChanged(result.user);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("error.changePassword"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="password-screen">
      <form className="password-card" onSubmit={submit}>
        <div className="password-icon" aria-hidden="true"><ShieldCheck size={26} /></div>
        <span className="section-eyebrow">{t("password.title")}</span>
        <h1>{t("password.eyebrow")}</h1>
        <p className="password-lede">
          {user.displayName}, the password you signed in with was generated for you and
          may have been sent over chat or email. Replace it before you continue.
        </p>

        <div className="form-field">
          <label htmlFor="current-password">{t("password.current")}</label>
          <input
            id="current-password" type="password" autoComplete="current-password" required
            value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="new-password">{t("password.new")}</label>
          <input
            id="new-password" type="password" autoComplete="new-password" required
            value={newPassword} onChange={(event) => setNewPassword(event.target.value)}
            aria-describedby="new-password-hint"
          />
          <small id="new-password-hint" className={tooShort ? "field-warning" : ""}>
            {tooShort ? t("password.charsNeeded", { n: 12 - newPassword.length }) : t("password.minLength")}
          </small>
        </div>

        <div className="form-field">
          <label htmlFor="confirm-password">{t("password.repeat")}</label>
          <input
            id="confirm-password" type="password" autoComplete="new-password" required
            value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)}
          />
          {mismatch && <small className="field-warning">{t("password.mustMatch")}</small>}
        </div>

        {asksConsent && (
          /* A radio group, not a checkbox: an unticked box cannot tell "no" apart
             from "has not read it yet", and this screen has to know the difference. */
          <fieldset className="consent-field">
            <legend><Mail size={15} aria-hidden="true" /> {t("consent.legend")}</legend>
            <p className="consent-lede">{t("consent.body")}</p>
            <label className={emailOptIn === true ? "consent-option selected" : "consent-option"}>
              <input
                type="radio" name="email-opt-in" checked={emailOptIn === true}
                onChange={() => setEmailOptIn(true)}
              />
              <span>{t("consent.yes")}</span>
            </label>
            <label className={emailOptIn === false ? "consent-option selected" : "consent-option"}>
              <input
                type="radio" name="email-opt-in" checked={emailOptIn === false}
                onChange={() => setEmailOptIn(false)}
              />
              <span>{t("consent.no")}</span>
            </label>
          </fieldset>
        )}

        {error && <p className="password-error" role="alert">{error}</p>}

        <button className="button button-primary password-submit" type="submit" disabled={!canSubmit}>
          {busy ? <><LoaderCircle size={17} className="spin" /> {t("password.saving")}</> : <><KeyRound size={17} /> {t("password.save")}</>}
        </button>

        <button type="button" className="password-signout" onClick={onLogout}>{t("password.signOutInstead")}</button>
      </form>
    </main>
  );
}
